import React, { useState, useContext, useEffect, useMemo } from "react";
import { Modal, Button, Form, Row, Col, Spinner, ProgressBar } from "react-bootstrap";
import CustomersContext from "../../context/otogas/customers/customersContext";
import StationsContext from "../../context/otogas/stations/stationsContext";

const PriceUpdateModal = ({ show, handleClose, handleSave }) => {
    const customersCtx = useContext(CustomersContext) || {};
    const stationsCtx = useContext(StationsContext) || {};

    const products = customersCtx.products || [];
    const listProducts = customersCtx.listProducts;
    const productsLoading = !!customersCtx.loading;
    const productsError = customersCtx.error;

    const stations = stationsCtx.stations || [];
    const listStations = stationsCtx.listStations;
    const stationsLoading = !!stationsCtx.loading;
    const stationsError = stationsCtx.error;

    // --- STEP STATE ---
    const [step, setStep] = useState(1); // 1: Dates, 2: Times, 3: Details

    // --- FORM STATE (split date/time then combine on submit) ---
    const [formData, setFormData] = useState({
        // step 1
        startDate: "",
        endDate: "",
        // step 2
        startClock: "",
        endClock: "",
        // step 3
        stationCodes: [],
        product: "",
        newPrice: "",
    });

    const [errors, setErrors] = useState({});
    const [stationFilter, setStationFilter] = useState("");

    // utils (inline)
    const pad = (n) => String(n).padStart(2, "0");
    const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const fmtTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    const combineDateTime = (dateStr, timeStr) => `${dateStr}T${timeStr || "00:00:00"}`;

    useEffect(() => {
        if (!show) return;

        try { typeof listProducts === "function" && listProducts(); } catch { }
        try { typeof listStations === "function" && listStations(); } catch { }

        // defaults
        setFormData((prev) => {
            const now = new Date();
            const plus1h = new Date(now.getTime() + 60 * 60 * 1000);
            return {
                ...prev,
                startDate: prev.startDate || fmtDate(now),
                endDate: prev.endDate || fmtDate(now),
                startClock: prev.startClock || fmtTime(now),
                endClock: prev.endClock || fmtTime(plus1h),
            };
        });

        setStep(1);
        setErrors({});
        setStationFilter("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // visible stations (filter)
    const visibleStations = useMemo(() => {
        const q = stationFilter.trim().toLowerCase();
        if (!q) return stations;
        return stations.filter((s) => {
            const label = (s?.name || s?.stationName || s?.code || s?.id || s?.value || "")
                .toString()
                .toLowerCase();
            return label.includes(q);
        });
    }, [stationFilter, stations]);

    // events
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const toggleStation = (code) => {
        setFormData((prev) => {
            const set = new Set(prev.stationCodes);
            set.has(code) ? set.delete(code) : set.add(code);
            return { ...prev, stationCodes: Array.from(set) };
        });
    };

    const selectAllVisible = () => {
        const allVisibleCodes = visibleStations.map((s, i) =>
            s?.code || s?.stationCode || s?.id || s?.value || String(i)
        );
        setFormData((prev) => ({
            ...prev,
            stationCodes: Array.from(new Set([...prev.stationCodes, ...allVisibleCodes])),
        }));
    };

    const clearAllStations = () => setFormData((prev) => ({ ...prev, stationCodes: [] }));

    // validation per step
    const validateStep = (s = step) => {
        const v = {};
        if (s === 1) {
            if (!formData.startDate) v.startDate = "Start date is required";
            if (!formData.endDate) v.endDate = "End date is required";
            if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
                v.endDate = "End date must be on/after start date";
            }
        }
        if (s === 2) {
            if (!formData.startClock) v.startClock = "Start time is required";
            if (!formData.endClock) v.endClock = "End time is required";
            // If same date, ensure end time after start time
            if (
                formData.startDate &&
                formData.endDate &&
                formData.startClock &&
                formData.endClock &&
                formData.startDate === formData.endDate
            ) {
                if (formData.endClock <= formData.startClock) {
                    v.endClock = "End time must be after start time for the same day";
                }
            }
        }
        if (s === 3) {
            if (!formData.stationCodes?.length) v.stationCodes = "Select at least one station";
            if (!formData.product) v.product = "Product is required";
            if (!formData.newPrice || Number(formData.newPrice) <= 0) v.newPrice = "Enter a valid price";
            // Final full datetime check
            const startDT = new Date(combineDateTime(formData.startDate, formData.startClock));
            const endDT = new Date(combineDateTime(formData.endDate, formData.endClock));
            if (isFinite(startDT) && isFinite(endDT) && endDT <= startDT) {
                v.endClock = "End must be after start";
            }
        }
        return v;
    };

    const goNext = () => {
        const v = validateStep(step);
        if (Object.keys(v).length) { setErrors(v); return; }
        setErrors({});
        setStep((s) => Math.min(3, s + 1));
    };

    const goBack = () => {
        setErrors({});
        setStep((s) => Math.max(1, s - 1));
    };

    const onSubmit = () => {
        const v = validateStep(3);
        if (Object.keys(v).length) { setErrors(v); setStep(3); return; }

        const payload = [{
            stationCodes: formData.stationCodes.join(","), // API expects string
            product: formData.product,
            newPrice: Number(formData.newPrice),
            startTime: combineDateTime(formData.startDate, formData.startClock), // 'YYYY-MM-DDTHH:mm:ss'
            endTime: combineDateTime(formData.endDate, formData.endClock),
        }];

        try { handleSave?.(payload); } catch { }
        handleClose?.();
    };

    const stepPercent = step === 1 ? 33 : step === 2 ? 66 : 100;
    const disableSave =
        step !== 3 ||
        productsLoading ||
        stationsLoading ||
        Object.keys(validateStep(3)).length > 0;

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title> Price Change Schedule</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <ProgressBar now={stepPercent} className="mb-3" />
                {(productsLoading || stationsLoading) && (
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <Spinner animation="border" size="sm" />
                        <span>Loading options…</span>
                    </div>
                )}
                {(productsError || stationsError) && (
                    <div className="text-danger mb-3">
                        {productsError ? String(productsError) : null}
                        {stationsError ? ` ${String(stationsError)}` : null}
                    </div>
                )}

                {/* STEP 1: DATES */}
                {step === 1 && (
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="startDate" className="mb-3">
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleChange}
                                        isInvalid={!!errors.startDate}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.startDate}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="endDate" className="mb-3">
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleChange}
                                        isInvalid={!!errors.endDate}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.endDate}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                )}

                {/* STEP 2: TIMES */}
                {step === 2 && (
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="startClock" className="mb-3">
                                    <Form.Label>Start Time</Form.Label>
                                    <Form.Control
                                        type="time"
                                        step="1" // show seconds
                                        name="startClock"
                                        value={formData.startClock}
                                        onChange={handleChange}
                                        isInvalid={!!errors.startClock}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.startClock}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="endClock" className="mb-3">
                                    <Form.Label>End Time</Form.Label>
                                    <Form.Control
                                        type="time"
                                        step="1" // show seconds
                                        name="endClock"
                                        value={formData.endClock}
                                        onChange={handleChange}
                                        isInvalid={!!errors.endClock}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.endClock}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <div className="text-muted">
                            Selected window:&nbsp;
                            <strong>
                                {new Date(`${formData.startDate}T${formData.startClock}`)
                                    .toLocaleString(undefined, {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                    })}
                            </strong>{" "}
                            →{" "}
                            <strong>
                                {new Date(`${formData.endDate}T${formData.endClock}`)
                                    .toLocaleString(undefined, {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                    })}
                            </strong>
                        </div>
                    </Form>
                )}

                {/* STEP 3: STATIONS + PRODUCT + PRICE */}
                {step === 3 && (
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-2" controlId="stationFilter">
                                    <Form.Label className="mb-1">Stations</Form.Label>
                                    <div className="d-flex gap-2 mb-2">
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            placeholder="Search stations…"
                                            value={stationFilter}
                                            onChange={(e) => setStationFilter(e.target.value)}
                                        />
                                        <Button size="sm" variant="outline-secondary" onClick={selectAllVisible}>
                                            Select All
                                        </Button>
                                        <Button size="sm" variant="outline-secondary" onClick={clearAllStations}>
                                            Clear
                                        </Button>
                                    </div>
                                    <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                                        {visibleStations.length === 0 ? (
                                            <div className="text-muted">No stations</div>
                                        ) : (
                                            visibleStations.map((s, i) => {
                                                const value = s?.code || s?.stationCode || s?.id || s?.value || String(i);
                                                const label = s?.name || s?.stationName || s?.code || s?.id || s?.value || `Station ${i + 1}`;
                                                const checked = formData.stationCodes.includes(value);
                                                return (
                                                    <Form.Check
                                                        key={value}
                                                        type="checkbox"
                                                        id={`st-${value}`}
                                                        label={label}
                                                        checked={checked}
                                                        onChange={() => toggleStation(value)}
                                                        className="mb-1"
                                                    />
                                                );
                                            })
                                        )}
                                    </div>
                                    {errors.stationCodes && <div className="invalid-feedback d-block">{errors.stationCodes}</div>}
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="product">
                                    <Form.Label>Product</Form.Label>
                                    <Form.Select
                                        name="product"
                                        value={formData.product}
                                        onChange={handleChange}
                                        isInvalid={!!errors.product}
                                        aria-label="Select product"
                                    >
                                        <option value="">-- Select product --</option>
                                        {(products || []).map((p, i) => {
                                            const value = p?.code || p?.productCode || p?.id || p?.value || String(i);
                                            const label = p?.name || p?.productName || p?.code || p?.id || p?.value || `Product ${i + 1}`;
                                            return (
                                                <option key={value} value={value}>{label}</option>
                                            );
                                        })}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{errors.product}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="newPrice">
                                    <Form.Label>New Price</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        name="newPrice"
                                        value={formData.newPrice}
                                        onChange={handleChange}
                                        isInvalid={!!errors.newPrice}
                                        placeholder="0.00"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.newPrice}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Modal.Body>

            <Modal.Footer>
                <div className="me-auto">Step {step} of 3</div>
                {step > 1 && (
                    <Button variant="outline-secondary" onClick={goBack}>
                        Back
                    </Button>
                )}
                {step < 3 && (
                    <Button variant="primary" onClick={goNext}>
                        Next
                    </Button>
                )}
                {step === 3 && (
                    <Button variant="primary" onClick={onSubmit} disabled={disableSave}>
                        Save
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default PriceUpdateModal;
